import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Wellsync-inspired color palette - mature corporate healthcare aesthetic
const COLORS = {
  primary: '#2563eb',      // Blue-600 (Wellsync primary)
  secondary: '#1e40af',    // Blue-800 (deeper blue)
  success: '#059669',      // Emerald-600 (deeper green)
  warning: '#d97706',      // Amber-600 (deeper amber)
  danger: '#dc2626',       // Red-600
  purple: '#7c3aed',       // Violet-600
  pink: '#db2777',         // Pink-600
  accent: '#0891b2',       // Cyan-600
};

const HealthScoreGauge = ({ score, label }) => {
  const getColor = (s) => {
    if (s >= 80) return '#10b981';
    if (s >= 60) return '#f59e0b';
    return '#ef4444';
  };
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="64" cy="64" r="56" stroke="#e5e7eb" strokeWidth="12" fill="none" />
          <circle 
            cx="64" cy="64" r="56" 
            stroke={getColor(score)} 
            strokeWidth="12" 
            fill="none" 
            strokeDasharray={`${(score / 100) * 352} 352`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-3xl font-bold text-gray-900">{score}%</span>
        </div>
      </div>
      <p className="mt-2 text-sm font-medium text-gray-600">{label}</p>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    healthy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
    maintenance: 'bg-blue-100 text-blue-700 border-blue-200',
  };
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.healthy}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const OEMHealthPortal = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [robotHealth, setRobotHealth] = useState([]);
  const [systemMetrics, setSystemMetrics] = useState({});
  const [errorTrends, setErrorTrends] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    loadHealthData();
  }, [dateRange]);

  const loadHealthData = async () => {
    setLoading(true);
    try {
      const [kpisData, robotsData, trendsData] = await Promise.all([
        analyticsAPI.getPerformanceKPIs(null, parseInt(dateRange)),
        analyticsAPI.getRobotPerformance(null, parseInt(dateRange)),
        analyticsAPI.getTrends(null, parseInt(dateRange)),
      ]);

      // Process robot health data - handle both direct response and nested data structure
      const robots = robotsData?.data?.robots || robotsData?.robots || [];
      const healthData = robots.map(robot => {
        const errorRate = robot.error_rate || (100 - (robot.success_rate || 0));
        const healthScore = Math.max(0, 100 - (errorRate * 2));
        let status = 'healthy';
        if (healthScore < 60) status = 'critical';
        else if (healthScore < 80) status = 'warning';
        
        return {
          ...robot,
          health_score: Math.round(healthScore),
          status,
          error_rate: errorRate,
          needs_maintenance: healthScore < 70 || robot.total_sessions > 1000,
          last_error: robot.last_error || 'None',
        };
      });

      setRobotHealth(healthData);

      // Calculate system-wide metrics
      const totalRobots = healthData.length;
      const healthyRobots = healthData.filter(r => r.status === 'healthy').length;
      const warningRobots = healthData.filter(r => r.status === 'warning').length;
      const criticalRobots = healthData.filter(r => r.status === 'critical').length;
      const avgHealthScore = totalRobots > 0 
        ? Math.round(healthData.reduce((sum, r) => sum + r.health_score, 0) / totalRobots)
        : 0;

      // Extract KPIs data - handle both direct response and nested data structure
      const kpis = kpisData?.data || kpisData || {};
      
      setSystemMetrics({
        total_robots: totalRobots,
        healthy_robots: healthyRobots,
        warning_robots: warningRobots,
        critical_robots: criticalRobots,
        avg_health_score: avgHealthScore,
        total_sessions: kpis.total_sessions || 0,
        success_rate: kpis.success_rate || 0,
        error_rate: kpis.error_rate || 0,
        uptime: 99.2,
        avg_session_duration: kpis.avg_duration || 0,
      });

      // Process error trends - handle both direct response and nested data structure
      const trends = trendsData?.data || trendsData || {};
      const daily = trends.daily || [];
      setErrorTrends(daily.map(d => ({
        date: d.date,
        errors: d.failed || 0,
        sessions: d.sessions || 0,
        error_rate: d.sessions > 0 ? ((d.failed / d.sessions) * 100).toFixed(1) : 0,
      })));

      // Generate maintenance alerts
      const alerts = healthData
        .filter(r => r.needs_maintenance || r.status === 'critical' || r.status === 'warning')
        .map(r => ({
          robot: r.serial || r.name,
          hospital: r.hospital || r.hospital_name || 'Unknown',
          issue: r.status === 'critical' ? 'High error rate - immediate attention required' 
               : r.status === 'warning' ? 'Elevated error rate - schedule maintenance'
               : 'Routine maintenance recommended',
          priority: r.status === 'critical' ? 'high' : r.status === 'warning' ? 'medium' : 'low',
          health_score: r.health_score,
        }));
      setMaintenanceAlerts(alerts);

    } catch (error) {
      console.error('Error loading health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportHealthReport = () => {
    const report = {
      generated_at: new Date().toISOString(),
      system_metrics: systemMetrics,
      robot_health: robotHealth,
      maintenance_alerts: maintenanceAlerts,
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oem-health-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="bg-white p-10 rounded-3xl shadow-xl border border-slate-100 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-700">Loading Health Assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Wellsync-style Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">OEM Health Assessment Portal</h1>
            <p className="text-slate-500 mt-1">Robot fleet health monitoring and maintenance insights</p>
          </div>
          <div className="flex items-center gap-4">
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-xl bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            >
              <option value="7">Last 7 Days</option>
              <option value="14">Last 14 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
            <button 
              onClick={exportHealthReport}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm shadow-blue-500/30 font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Report
            </button>
          </div>
        </div>

        {/* Wellsync-style System Health Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Fleet Health Score</h3>
            <HealthScoreGauge score={systemMetrics.avg_health_score} label="Overall Health" />
          </div>
          
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">System Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{systemMetrics.total_robots}</p>
                <p className="text-sm text-gray-500">Total Robots</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-3xl font-bold text-emerald-600">{systemMetrics.healthy_robots}</p>
                <p className="text-sm text-gray-500">Healthy</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="text-3xl font-bold text-amber-600">{systemMetrics.warning_robots}</p>
                <p className="text-sm text-gray-500">Warning</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{systemMetrics.critical_robots}</p>
                <p className="text-sm text-gray-500">Critical</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{systemMetrics.total_sessions?.toLocaleString()}</p>
                <p className="text-sm text-gray-500">Total Sessions</p>
              </div>
              <div className="text-center p-4 bg-cyan-50 rounded-lg">
                <p className="text-2xl font-bold text-cyan-600">{systemMetrics.success_rate}%</p>
                <p className="text-sm text-gray-500">Success Rate</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{systemMetrics.uptime}%</p>
                <p className="text-sm text-gray-500">System Uptime</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <p className="text-2xl font-bold text-pink-600">{systemMetrics.avg_session_duration} min</p>
                <p className="text-sm text-gray-500">Avg Duration</p>
              </div>
            </div>
          </div>
        </div>

        {/* Wellsync-style Error Trends Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Error Trend Analysis</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={errorTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="errors" name="Errors" stroke={COLORS.danger} strokeWidth={2} dot={{ fill: COLORS.danger }} />
                <Line type="monotone" dataKey="sessions" name="Sessions" stroke={COLORS.primary} strokeWidth={2} dot={{ fill: COLORS.primary }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Robot Health Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Healthy', value: systemMetrics.healthy_robots, color: COLORS.success },
                    { name: 'Warning', value: systemMetrics.warning_robots, color: COLORS.warning },
                    { name: 'Critical', value: systemMetrics.critical_robots, color: COLORS.danger },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {[COLORS.success, COLORS.warning, COLORS.danger].map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Wellsync-style Maintenance Alerts */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Maintenance Alerts</h3>
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
              {maintenanceAlerts.filter(a => a.priority === 'high').length} Critical
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Robot</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Hospital</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Issue</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Health Score</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Priority</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Action</th>
                </tr>
              </thead>
              <tbody>
                {maintenanceAlerts.slice(0, 10).map((alert, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium">{alert.robot}</td>
                    <td className="py-3 px-4 text-gray-600">{alert.hospital}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">{alert.issue}</td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${alert.health_score >= 80 ? 'text-emerald-600' : alert.health_score >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                        {alert.health_score}%
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        alert.priority === 'high' ? 'bg-red-100 text-red-700' :
                        alert.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {alert.priority.charAt(0).toUpperCase() + alert.priority.slice(1)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                        Schedule Service
                      </button>
                    </td>
                  </tr>
                ))}
                {maintenanceAlerts.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-500">
                      No maintenance alerts at this time
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Robot Fleet Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Robot Fleet Health Details</h3>
            <button 
              onClick={() => {
                const csv = [
                  ['Robot', 'Hospital', 'Health Score', 'Status', 'Sessions', 'Success Rate', 'Error Rate'].join(','),
                  ...robotHealth.map(r => [
                    r.serial || r.name,
                    r.hospital || r.hospital_name || 'Unknown',
                    r.health_score,
                    r.status,
                    r.total_sessions,
                    r.success_rate,
                    r.error_rate
                  ].join(','))
                ].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'robot-fleet-health.csv';
                a.click();
              }}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
            >
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Robot</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Hospital</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Health Score</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Status</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Sessions</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Success Rate</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Error Rate</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium text-sm">Avg Duration</th>
                </tr>
              </thead>
              <tbody>
                {robotHealth.map((robot, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium">{robot.serial || robot.name}</td>
                    <td className="py-3 px-4 text-gray-600">{robot.hospital || robot.hospital_name || 'Unknown'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${robot.health_score >= 80 ? 'bg-emerald-500' : robot.health_score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${robot.health_score}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{robot.health_score}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={robot.status} />
                    </td>
                    <td className="py-3 px-4 text-gray-600">{robot.total_sessions?.toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${robot.success_rate >= 80 ? 'text-emerald-600' : robot.success_rate >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                        {robot.success_rate}%
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${robot.error_rate <= 20 ? 'text-emerald-600' : robot.error_rate <= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                        {robot.error_rate?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{robot.avg_duration?.toFixed(1)} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-400 text-sm">
          <p>OEM Health Assessment Portal v1.0 | Data refreshed: {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default OEMHealthPortal;
